import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Alert, Badge, Button, Card, Spinner } from "../../../components/ui";
import { AppShell } from "../../../layouts/AppShell";
import type { VoiceConversationConfig } from "../api/voiceApi";
import { getVoiceConversationConfig } from "../api/voiceApi";
import {
  MIN_VOICE_MS_DEFAULT,
  RMS_THRESHOLD_DEFAULT,
  SILENCE_TIMEOUT_MS_DEFAULT,
  useAudioRecorder,
} from "../hooks/useAudioRecorder";

type ConnectionState = "idle" | "connecting" | "open" | "closed" | "error";
const PLAYBACK_BUFFER_TARGET_MS = 1000;

type AssistantMessage = {
  id: string;
  text: string;
};

type ToolActivity = {
  id: string;
  name: string;
  state: string;
  query: string | null;
};

type RealtimeEvent = {
  type: string;
  turn_id?: string;
  data?: Record<string, unknown>;
};

function websocketUrl(): string {
  if (typeof window === "undefined") {
    return "/api/v1/voice/realtime";
  }
  const url = new URL("/api/v1/voice/realtime", window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

function badgeVariant(active: boolean): string {
  return active ? "success" : "warning";
}

function connectionBadgeVariant(state: ConnectionState): string {
  if (state === "open") {
    return "success";
  }
  if (state === "connecting") {
    return "info";
  }
  if (state === "error") {
    return "danger";
  }
  return "secondary";
}

function microphoneBadgeVariant(state: string): string {
  if (state === "recording") {
    return "success";
  }
  if (state === "requesting") {
    return "info";
  }
  if (state === "error") {
    return "danger";
  }
  return "secondary";
}

function readinessSummary(config: VoiceConversationConfig): string {
  return config.ready ? "可啟動對話" : "請先完成設定頁的必要準備";
}

function createPlaybackAudioContext(options?: AudioContextOptions): AudioContext {
  return new AudioContext(options);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function concatBytes(chunks: Uint8Array[]): Uint8Array {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
  const merged = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return merged;
}

function pcm16ToAudioBuffer(audioContext: AudioContext, pcmBytes: Uint8Array, sampleRate: number): AudioBuffer {
  const frameCount = Math.floor(pcmBytes.byteLength / 2);
  const audioBuffer = audioContext.createBuffer(1, frameCount, sampleRate);
  const channelData = audioBuffer.getChannelData(0);
  const view = new DataView(pcmBytes.buffer, pcmBytes.byteOffset, pcmBytes.byteLength);
  for (let index = 0; index < frameCount; index += 1) {
    channelData[index] = view.getInt16(index * 2, true) / 0x8000;
  }
  return audioBuffer;
}

export function VoiceConversationPage() {
  const configQuery = useQuery({
    queryKey: ["voice", "conversation-config"],
    queryFn: getVoiceConversationConfig,
  });

  const socketRef = useRef<WebSocket | null>(null);
  const messageIdRef = useRef(0);
  const assistantMessageIdRef = useRef<string | null>(null);
  const assistantTextRef = useRef("");
  const playbackContextRef = useRef<AudioContext | null>(null);
  const playbackSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const playbackPendingChunksRef = useRef(0);
  const playbackDoneAwaitingRef = useRef(false);
  const playbackGenerationRef = useRef(0);
  const playbackQueueRef = useRef(Promise.resolve());
  const playbackBufferedChunksRef = useRef<Uint8Array[]>([]);
  const playbackBufferedBytesRef = useRef(0);

  const [connectionState, setConnectionState] = useState<ConnectionState>("idle");
  const [gatewayState, setGatewayState] = useState("idle");
  const [sessionReady, setSessionReady] = useState(false);
  const [inputLocked, setInputLocked] = useState(false);
  const [autoConversationEnabled, setAutoConversationEnabled] = useState(false);
  const [awaitingTurnCompletion, setAwaitingTurnCompletion] = useState(false);
  const [assistantMessages, setAssistantMessages] = useState<AssistantMessage[]>([]);
  const [toolActivities, setToolActivities] = useState<ToolActivity[]>([]);
  const [rateLimitSummary, setRateLimitSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recordingStoppedNotice, setRecordingStoppedNotice] = useState<string | null>(null);

  const recorder = useAudioRecorder({
    onChunk: (audioBase64) => {
      sendPayload({ type: "audio.append", audio: audioBase64 });
    },
    onSilenceTimeout: () => {
      setAutoConversationEnabled(false);
      setAwaitingTurnCompletion(false);
      setInputLocked(false);
      setGatewayState("ready");
      setRecordingStoppedNotice("已停止收音，請等待助教回應完成後，再按「開始語音對話」重新開始。");
    },
    silenceTimeoutMs: SILENCE_TIMEOUT_MS_DEFAULT,
    rmsThreshold: RMS_THRESHOLD_DEFAULT,
    minVoiceMs: MIN_VOICE_MS_DEFAULT,
    targetSampleRate: configQuery.data?.audio.sample_rate ?? 24000,
  });

  function nextId(prefix: string): string {
    messageIdRef.current += 1;
    return `${prefix}-${messageIdRef.current}`;
  }

  function ensureAssistantMessage() {
    if (assistantMessageIdRef.current) {
      return assistantMessageIdRef.current;
    }
    const id = nextId("assistant");
    assistantMessageIdRef.current = id;
    assistantTextRef.current = "";
    setAssistantMessages((current) => [...current, { id, text: "" }]);
    return id;
  }

  function updateAssistantMessage(text: string, replace = false) {
    const id = ensureAssistantMessage();
    assistantTextRef.current = replace ? text : `${assistantTextRef.current}${text}`;
    setAssistantMessages((current) =>
      current.map((message) =>
        message.id === id ? { ...message, text: assistantTextRef.current } : message,
      ),
    );
  }

  function finishAssistantTurn() {
    assistantMessageIdRef.current = null;
    assistantTextRef.current = "";
  }

  function sendPayload(payload: Record<string, unknown>) {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return false;
    }
    socket.send(JSON.stringify(payload));
    return true;
  }

  function maybeReportPlaybackDone() {
    if (
      !playbackDoneAwaitingRef.current ||
      playbackPendingChunksRef.current > 0 ||
      playbackBufferedBytesRef.current > 0
    ) {
      return;
    }
    playbackDoneAwaitingRef.current = false;
    sendPayload({ type: "playback.done" });
  }

  function stopPlayback() {
    playbackGenerationRef.current += 1;
    playbackDoneAwaitingRef.current = false;
    playbackPendingChunksRef.current = 0;
    playbackSourceRef.current?.stop();
    playbackSourceRef.current?.disconnect();
    playbackSourceRef.current = null;
    playbackQueueRef.current = Promise.resolve();
    playbackBufferedChunksRef.current = [];
    playbackBufferedBytesRef.current = 0;

    if (playbackContextRef.current && playbackContextRef.current.state !== "closed") {
      void playbackContextRef.current.close();
    }
    playbackContextRef.current = null;
  }

  function playbackTargetBytes(sampleRate: number, channels: number) {
    const bytesPerSample = 2;
    return Math.floor(sampleRate * channels * bytesPerSample * (PLAYBACK_BUFFER_TARGET_MS / 1000));
  }

  function enqueuePlaybackBytes(pcmBytes: Uint8Array, sampleRate: number) {
    const config = configQuery.data;
    if (!config) {
      return;
    }

    const generation = playbackGenerationRef.current;
    playbackPendingChunksRef.current += 1;
    playbackQueueRef.current = playbackQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        if (playbackGenerationRef.current !== generation) {
          return;
        }
        const audioContext =
          playbackContextRef.current ??
          createPlaybackAudioContext({ sampleRate: config.audio.sample_rate });
        playbackContextRef.current = audioContext;
        if (audioContext.state === "suspended") {
          await audioContext.resume();
        }

        const buffer = pcm16ToAudioBuffer(
          audioContext,
          pcmBytes,
          sampleRate,
        );
        await new Promise<void>((resolve) => {
          const source = audioContext.createBufferSource();
          playbackSourceRef.current = source;
          source.buffer = buffer;
          source.connect(audioContext.destination);
          source.onended = () => {
            if (playbackSourceRef.current === source) {
              playbackSourceRef.current = null;
            }
            source.disconnect();
            resolve();
          };
          source.start();
        });
      })
      .catch(() => {
        setError("AI 音訊播放失敗，請重新開始對話。");
      })
      .finally(() => {
        playbackPendingChunksRef.current = Math.max(0, playbackPendingChunksRef.current - 1);
        maybeReportPlaybackDone();
      });
  }

  function flushBufferedPlayback(force = false) {
    const config = configQuery.data;
    if (!config || playbackBufferedBytesRef.current === 0) {
      return;
    }
    const targetBytes = playbackTargetBytes(config.audio.sample_rate, config.audio.channels);
    if (!force && playbackBufferedBytesRef.current < targetBytes) {
      return;
    }
    const merged = concatBytes(playbackBufferedChunksRef.current);
    playbackBufferedChunksRef.current = [];
    playbackBufferedBytesRef.current = 0;
    enqueuePlaybackBytes(merged, config.audio.sample_rate);
  }

  function queuePlaybackChunk(base64Audio: string) {
    const config = configQuery.data;
    if (!config) {
      return;
    }
    const pcmBytes = base64ToBytes(base64Audio);
    playbackBufferedChunksRef.current.push(pcmBytes);
    playbackBufferedBytesRef.current += pcmBytes.byteLength;
    const targetBytes = playbackTargetBytes(config.audio.sample_rate, config.audio.channels);
    if (playbackBufferedBytesRef.current >= targetBytes) {
      flushBufferedPlayback(true);
    }
  }

  function stopRecording() {
    recorder.stop();
  }

  function disconnect(showSystemMessage: boolean) {
    setAutoConversationEnabled(false);
    stopRecording();
    stopPlayback();

    const socket = socketRef.current;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: "session.stop" }));
      }
      socket.close();
      socketRef.current = null;
    }

    setConnectionState("closed");
    setSessionReady(false);
    setInputLocked(false);
    setAwaitingTurnCompletion(false);
    setGatewayState("closed");
    finishAssistantTurn();
    if (showSystemMessage) {
      setError("已中斷與語音助教的連線。");
    }
    setRecordingStoppedNotice(null);
  }

  function connect() {
    if (!configQuery.data?.ready || connectionState === "connecting" || connectionState === "open") {
      return;
    }

    setError(null);
    setConnectionState("connecting");
    setGatewayState("connecting");

    const socket = new WebSocket(websocketUrl());
    socketRef.current = socket;

    socket.onopen = () => {
      setConnectionState("open");
      setGatewayState("starting");
      socket.send(JSON.stringify({ type: "session.start" }));
    };

    socket.onmessage = (messageEvent) => {
      let event: RealtimeEvent;
      try {
        event = JSON.parse(String(messageEvent.data)) as RealtimeEvent;
      } catch {
        setError("Realtime 回傳了無法解析的資料。");
        return;
      }

      const data = event.data ?? {};
      switch (event.type) {
        case "session.status":
          setGatewayState(String(data.state ?? "unknown"));
          if (data.state === "closed") {
            setSessionReady(false);
            setInputLocked(false);
            setAwaitingTurnCompletion(false);
            stopRecording();
          }
          break;
        case "session.ready":
          setSessionReady(true);
          setAwaitingTurnCompletion(false);
          setGatewayState("ready");
          setError(null);
          break;
        case "input.locked":
          setInputLocked(true);
          setAwaitingTurnCompletion(true);
          stopRecording();
          break;
        case "input.unlocked":
          setInputLocked(false);
          setAwaitingTurnCompletion(false);
          break;
        case "assistant.response.started":
          ensureAssistantMessage();
          break;
        case "assistant.transcript.delta":
          updateAssistantMessage(String(data.text ?? ""));
          break;
        case "assistant.transcript.final":
          if (!assistantTextRef.current.trim()) {
            updateAssistantMessage(String(data.text ?? ""), true);
          }
          break;
        case "assistant.audio.delta":
          if (typeof data.audio === "string" && data.audio) {
            queuePlaybackChunk(data.audio);
          }
          break;
        case "assistant.audio.done":
          flushBufferedPlayback(true);
          playbackDoneAwaitingRef.current = true;
          maybeReportPlaybackDone();
          break;
        case "assistant.response.done":
          if (!assistantTextRef.current.trim()) {
            updateAssistantMessage("助教已完成回應，但本次未收到文字逐字稿。", true);
          }
          finishAssistantTurn();
          break;
        case "tool.status":
          setToolActivities((current) => [
            {
              id: nextId("tool"),
              name: String(data.name ?? "unknown"),
              state: String(data.state ?? "unknown"),
              query: typeof data.query === "string" && data.query.trim() ? data.query : null,
            },
            ...current,
          ]);
          break;
        case "usage.updated":
          if (Array.isArray(data.rate_limits)) {
            const summary = data.rate_limits
              .map((item) => {
                if (!item || typeof item !== "object") {
                  return null;
                }
                const record = item as Record<string, unknown>;
                const name = String(record.name ?? "unknown");
                const remaining = String(record.remaining ?? "-");
                return `${name}: ${remaining}`;
              })
              .filter(Boolean)
              .join(" / ");
            setRateLimitSummary(summary || null);
          }
          break;
        case "error":
          setError(String(data.message ?? "語音助教發生錯誤。"));
          stopRecording();
          break;
        default:
          break;
      }
    };

    socket.onerror = () => {
      setAutoConversationEnabled(false);
      setConnectionState("error");
      setError("Realtime 連線失敗，請確認登入狀態與後端服務是否正常。");
      stopRecording();
    };

    socket.onclose = () => {
      socketRef.current = null;
      setAutoConversationEnabled(false);
      setConnectionState((current) => (current === "error" ? current : "closed"));
      setSessionReady(false);
      setInputLocked(false);
      setAwaitingTurnCompletion(false);
      setGatewayState("closed");
      stopRecording();
      stopPlayback();
      finishAssistantTurn();
    };
  }

  async function startRecording() {
    const config = configQuery.data;
    if (!config || !sessionReady || inputLocked || recorder.status === "recording") {
      return;
    }

    try {
      setError(null);
      setRecordingStoppedNotice(null);
      setAwaitingTurnCompletion(false);
      setGatewayState("listening");
      sendPayload({ type: "audio.clear" });
      await recorder.start({ sampleRate: config.audio.sample_rate });
    } catch (caughtError) {
      setAutoConversationEnabled(false);
      setError(
        caughtError instanceof Error ? caughtError.message : "無法啟動麥克風，請檢查瀏覽器權限。",
      );
    }
  }

  function startConversation() {
    setAutoConversationEnabled(true);
    setError(null);
    setRecordingStoppedNotice(null);

    if (connectionState === "open") {
      if (inputLocked) {
        setAutoConversationEnabled(false);
        setRecordingStoppedNotice("目前仍在等待助教完成回應，請稍候再重新開始收音。");
        return;
      }
      if (sessionReady && !inputLocked && recorder.status === "idle") {
        void startRecording();
      }
      return;
    }

    connect();
  }

  useEffect(() => {
    if (
      !autoConversationEnabled ||
      connectionState !== "open" ||
      !sessionReady ||
      inputLocked ||
      awaitingTurnCompletion ||
      recorder.status !== "idle"
    ) {
      return;
    }
    void startRecording();
  }, [autoConversationEnabled, awaitingTurnCompletion, connectionState, inputLocked, recorder.status, sessionReady]);

  useEffect(() => {
    return () => {
      stopRecording();
      stopPlayback();

      const socket = socketRef.current;
      if (!socket) {
        return;
      }
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.close();
      socketRef.current = null;
    };
  }, []);

  return (
    <AppShell title="AI 語音對話" breadcrumb={["AI 語音助教", "AI 語音對話"]}>
      <Alert variant="info">
        這個頁面專門用於語音助教對話。API Key、RAG 與 Persona 請在{" "}
        <Link to="/voice">設定頁</Link>{" "}
        先完成。
      </Alert>
      {configQuery.isLoading && <Spinner label="載入對話設定" />}
      {configQuery.isError && <Alert variant="danger">無法載入對話設定，請稍後再試。</Alert>}
      {!configQuery.isLoading && !configQuery.isError && configQuery.data && (
        <>
          <Card title="對話前檢查">
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Badge variant={badgeVariant(configQuery.data.requirements.api_key_ready)}>
                API Key {configQuery.data.requirements.api_key_ready ? "已就緒" : "未完成"}
              </Badge>
              <Badge variant={badgeVariant(configQuery.data.requirements.rag_ready)}>
                RAG {configQuery.data.requirements.rag_ready ? "已就緒" : "未完成"}
              </Badge>
              <Badge variant={badgeVariant(configQuery.data.requirements.persona_ready)}>
                Persona {configQuery.data.requirements.persona_ready ? "已就緒" : "未完成"}
              </Badge>
              <Badge variant={connectionBadgeVariant(connectionState)}>連線 {connectionState}</Badge>
              <Badge variant={microphoneBadgeVariant(recorder.status)}>麥克風 {recorder.status}</Badge>
            </div>
            <dl className="mb-3">
              <dt className="small text-muted mb-1">整體狀態</dt>
              <dd>{readinessSummary(configQuery.data)}</dd>
              <dt className="small text-muted mb-1">Gateway 狀態</dt>
              <dd>{gatewayState}</dd>
              <dt className="small text-muted mb-1">音訊格式</dt>
              <dd>
                {configQuery.data.audio.format} / {configQuery.data.audio.sample_rate} Hz /{" "}
                {configQuery.data.audio.channels} 聲道
              </dd>
              <dt className="small text-muted mb-1">語音設定</dt>
              <dd>
                {configQuery.data.voice} ({configQuery.data.voiceGender})
              </dd>
              <dt className="small text-muted mb-1">助教名稱</dt>
              <dd>{configQuery.data.assistantName}</dd>
              <dt className="small text-muted mb-1">連線端點</dt>
              <dd className="mb-0">
                <code>{websocketUrl()}</code>
              </dd>
            </dl>
            {!configQuery.data.ready && (
              <Alert variant="warning">請先回設定頁完成 API Key、RAG 與 Persona，才能啟動對話。</Alert>
            )}
            <div className="d-flex flex-wrap gap-2">
              <Button
                onClick={startConversation}
                disabled={
                  !configQuery.data.ready ||
                  autoConversationEnabled ||
                  connectionState === "connecting"
                }
              >
                開始語音對話
              </Button>
              <Button
                variant="secondary"
                onClick={() => disconnect(true)}
                disabled={!autoConversationEnabled && connectionState !== "connecting"}
              >
                結束語音對話
              </Button>
            </div>
          </Card>

          {error && <Alert variant="danger">{error}</Alert>}

          <Card title="語音輸入">
            <p className="text-muted">
              使用者語音會以 <code>pcm16 / 24000 Hz / 1 聲道</code> 傳給 backend realtime gateway；
              畫面只顯示 AI 回答文字，不顯示使用者語音轉譯。
            </p>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <Badge variant={autoConversationEnabled ? "success" : "secondary"}>
                {autoConversationEnabled ? "已啟用自動收音循環" : "尚未開始對話"}
              </Badge>
              <Badge variant={inputLocked ? "warning" : "success"}>
                {inputLocked ? "助教回應中" : "可開始說話"}
              </Badge>
              <Badge variant={recorder.recording ? "info" : "secondary"}>
                {recorder.recording
                  ? `靜音倒數 ${recorder.silenceRemaining}s`
                  : `RMS 門檻 ${RMS_THRESHOLD_DEFAULT.toFixed(3)}`}
              </Badge>
            </div>
            <p className="small text-muted mb-0">
              按下「開始語音對話」後會自動開始收音。前端會先做 RMS 靜音偵測，只有超過門檻且累積語音超過 {MIN_VOICE_MS_DEFAULT}
              ms 才會開始把音訊送到 realtime；若連續靜音超過 {SILENCE_TIMEOUT_MS_DEFAULT} ms，會停止本輪收音並等待 assistant 回應。assistant 回應期間不允許插話，必須等前端音訊播放完成並收到解鎖事件後，才會自動進入下一輪收音。
            </p>
            <p className="small text-muted mb-0">
              為降低逐 chunk 播放造成的斷裂感，前端會先累積約 {PLAYBACK_BUFFER_TARGET_MS / 1000} 秒的 AI 音訊再開始播放；若尾段不足 1 秒但已收到音訊結束事件，仍會立即播完剩餘內容。
            </p>
          </Card>

          <Card title="AI 回答文字">
            <div className="border rounded p-3 bg-white" style={{ minHeight: 240 }}>
              {recordingStoppedNotice && <Alert variant="warning">{recordingStoppedNotice}</Alert>}
              {assistantMessages.length === 0 && <p className="text-muted mb-0">尚無 AI 回答。</p>}
              {assistantMessages.map((message, index) => (
                <article key={message.id} className="mb-3">
                  <div className="small text-muted mb-1">回答 {index + 1}</div>
                  <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {message.text || " "}
                  </p>
                </article>
              ))}
            </div>
          </Card>

          <Card title="工具與限流狀態">
            {rateLimitSummary && <p className="small text-muted">Rate limits: {rateLimitSummary}</p>}
            {toolActivities.length === 0 ? (
              <p className="text-muted mb-0">尚未觸發知識庫查詢工具。</p>
            ) : (
              <div className="d-flex flex-column gap-2">
                {toolActivities.slice(0, 6).map((activity) => (
                  <div
                    key={activity.id}
                    className="border rounded p-2 bg-white d-flex justify-content-between align-items-start gap-3"
                  >
                    <div>
                      <div>{activity.name}</div>
                      {activity.query && (
                        <div className="small text-muted">
                          query: <code>{activity.query}</code>
                        </div>
                      )}
                    </div>
                    <Badge
                      variant={
                        activity.state === "completed"
                          ? "success"
                          : activity.state === "failed"
                            ? "danger"
                            : "info"
                      }
                    >
                      {activity.state}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </AppShell>
  );
}
